import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ticketService } from '@/services/ticketService';
import { useProjectStore } from '@/store/projectStore';
import { Button } from '@/components/common/Button';
import { Table } from '@/components/common/Table';
import { Badge } from '@/components/common/Badge';
import { Avatar } from '@/components/common/Avatar';
import { CreateTicketModal } from '@/components/tickets/CreateTicketModal';
import { TicketListItem } from '@/types/ticket';
import { formatRelativeTime, formatStatus, formatPriority, getStatusColor, getPriorityColor } from '@/utils/formatters';
import Select from 'react-select';
import { userService } from '@/services/userService';

const FILTER_STORAGE_KEY = (projectId: string) => `tickets_filters_${projectId}`;

function loadStoredFilters(projectId: string) {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY(projectId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveFilters(projectId: string, filters: any, searchQuery: string, showFilters: boolean) {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY(projectId), JSON.stringify({ filters, searchQuery, showFilters }));
  } catch {
    // ignore storage errors
  }
}

export function TicketsPage() {
  const navigate = useNavigate();
  const { currentProject } = useProjectStore();

  const storedProjectId = currentProject?.id ? String(currentProject.id) : null;
  const stored = storedProjectId ? loadStoredFilters(storedProjectId) : null;

  const [filters, setFilters] = useState<any>(stored?.filters ?? {});
  const [searchQuery, setSearchQuery] = useState<string>(stored?.searchQuery ?? '');
  const [searchDebounce, setSearchDebounce] = useState<any>(null);
  const [showFilters, setShowFilters] = useState<boolean>(stored?.showFilters ?? false);

  const [users, setUsers] = useState([]);
  
  useEffect(() => {
      userService.getUsers().then((res) => {
        setUsers(res || []);
      });
    }, []);

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    if (!storedProjectId) return;
    saveFilters(storedProjectId, filters, searchQuery, showFilters);
  }, [filters, searchQuery, showFilters, storedProjectId]);

  // When project changes, load stored filters for new project
  useEffect(() => {
    if (!storedProjectId) return;
    const s = loadStoredFilters(storedProjectId);
    setFilters(s?.filters ?? {});
    setSearchQuery(s?.searchQuery ?? '');
    setShowFilters(s?.showFilters ?? false);
  }, [storedProjectId]);

  const assigneeOptions = users.map((u: any) => ({
    value: u.id,
    label: u.first_name + ' ' + u.last_name,
  }));
  

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'triaged', label: 'Triaged' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'waiting_customer', label: 'Waiting Customer' },
    { value: 'waiting_internal', label: 'Waiting Internal' },
    { value: 'in_review', label: 'In Review' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  const priorityOptions = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  const typeOptions = [
    { value: 'bug', label: 'Bug' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'support_ticket', label: 'Support Ticket' },
    { value: 'task', label: 'Task' },
    { value: 'epic', label: 'Epic' },
  ];

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (searchDebounce) clearTimeout(searchDebounce);
    setSearchDebounce(setTimeout(() => {
      setFilters((prev: any) => ({ ...prev, search: value || undefined }));
    }, 300));
  };
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: currentProject
      ? ['tickets', 'list', currentProject.id, filters, searchQuery]
      : ['tickets', 'list'],
    queryFn: () => {
      if (!currentProject) return null;
      const params = { ...filters };
      console.log('Fetching tickets with params:', params);
      if (searchQuery.trim()) params.search = searchQuery.trim();
      return ticketService.getTickets(currentProject.id, params);
    },
    enabled: !!currentProject,
  });

  const columns = [
    {
      key: 'ticket_key',
      title: 'ID',
      sortable: true,
      render: (ticket: TicketListItem) => (
        <span className="font-medium text-primary-600 flex items-center gap-1">
          {ticket.ticket_key}
          {(ticket as any).has_active_bell && (
            <span title="New comment â not yet seen by both teams" className="text-amber-500 text-sm leading-none">ð</span>
          )}
        </span>
      ),
    },
    {
      key: 'title',
      title: 'Title',
      sortable: true,
      className: 'min-w-[300px]',
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (ticket: TicketListItem) => (
        <Badge variant={getStatusColor(ticket.status)}>
          {formatStatus(ticket.status)}
        </Badge>
      ),
    },
    {
      key: 'priority',
      title: 'Priority',
      sortable: true,
      render: (ticket: TicketListItem) => (
        <Badge variant={getPriorityColor(ticket.priority)}>
          {formatPriority(ticket.priority)}
        </Badge>
      ),
    },
    {
      key: 'assignee',
      title: 'Assignee',
      render: (ticket: TicketListItem) => (
        ticket.assignee ? (
          <div className="flex items-center gap-2">
            <Avatar
              src={ticket.assignee.avatar_url}
              name={ticket.assignee.name}
              size="xs"
            />
            <span className="text-sm">{ticket.assignee.name}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">Unassigned</span>
        )
      ),
    },
    {
      key: 'updated_at',
      title: 'Updated',
      sortable: true,
      render: (ticket: TicketListItem) => (
        <span className="text-sm text-gray-600">
          {formatRelativeTime(ticket.updated_at)}
        </span>
      ),
    },
  ];

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-gray-500 mb-4">No project selected</p>
          <p className="text-sm text-gray-400">Please select a project to view tickets</p>
        </div>
      </div>
    );
  }

  const hasActiveFilters = Object.keys(filters).some(
    (k) => k !== 'search' && filters[k] !== undefined
  ) || searchQuery.trim() !== '';

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Tickets</h1>
            {currentProject && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                {currentProject.name}
              </span>
            )}
          </div>
          <p className="mt-2 text-gray-600">
            Manage and track all support tickets
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search tickets..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64"
            />
          </div>
          <Button
            variant="secondary"
            leftIcon={<FunnelIcon className="h-5 w-5" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filter{hasActiveFilters && <span className="ml-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-primary-500" />}
          </Button>
          <Button
            variant="primary"
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            New Ticket
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <Select
              isMulti
              options={statusOptions}
              placeholder="Select status..."
              classNamePrefix="react-select"
              className="text-sm"
              value={statusOptions.filter((o) =>
                filters.status?.includes(o.value)
              )}
              onChange={(selected) => {
                setFilters((prev: any) => ({
                  ...prev,
                  status:
                    selected.length > 0
                      ? selected.map((s: any) => s.value)
                      : undefined,
                }));
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
            <Select
              isMulti
              options={priorityOptions}
              placeholder="Select priority..."
              classNamePrefix="react-select"
              className="text-sm"
              value={priorityOptions.filter((o) =>
                filters.priority?.includes(o.value)
              )}
              onChange={(selected) => {
                setFilters((prev: any) => ({
                  ...prev,
                  priority:
                    selected && selected.length > 0
                      ? selected.map((s: any) => s.value)
                      : undefined,
                }));
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Assignee</label>
            <Select
              isMulti
              options={assigneeOptions}
              placeholder="Select assignee..."
              classNamePrefix="react-select"
              className="text-sm"
              value={assigneeOptions.filter((o) =>
                filters.assignee_id?.includes(o.value)
              )}
              onChange={(selected) => {
                setFilters((prev: any) => ({
                  ...prev,
                  assignee_id:
                    selected && selected.length > 0
                      ? selected.map((s: any) => s.value)
                      : undefined,
                }));
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
            <Select
              isMulti
              options={typeOptions}
              placeholder="Select type..."
              classNamePrefix="react-select"
              className="text-sm"
              value={typeOptions.filter((o) =>
                filters.type?.includes(o.value)
              )}
              onChange={(selected) => {
                setFilters((prev: any) => ({
                  ...prev,
                  type:
                    selected && selected.length > 0
                      ? selected.map((s: any) => s.value)
                      : undefined,
                }));
              }}
            />
          </div>
          <button
            onClick={() => {
              setFilters({});
              setSearchQuery('');
            }}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <Table
          columns={columns}
          data={data?.data || []}
          keyExtractor={(ticket) => ticket.id}
          onRowClick={(ticket) => navigate(`/tickets/${ticket.id}`)}
          isLoading={isLoading}
          emptyMessage="No tickets found"
        />
      </div>

      {currentProject && (
        <CreateTicketModal
          projectId={currentProject.id}
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
    </div>
  );
}

export default TicketsPage;
